import { lazy, Suspense } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { ScopeBand } from './components/ScopeBand';
import { ScrollToTop } from './components/ScrollToTop';
import { Home } from './pages/Home';

// Home stays in the main bundle — it's the LCP target and the most-visited
// route. Everything else is route-split so the first paint isn't blocked
// by code the visitor may never run.
const About            = lazy(() => import('./pages/About').then(m => ({ default: m.About })));
const Programs         = lazy(() => import('./pages/Programs').then(m => ({ default: m.Programs })));
const ProgramDetail    = lazy(() => import('./pages/ProgramDetail').then(m => ({ default: m.ProgramDetail })));
const BookConsult      = lazy(() => import('./pages/BookConsult').then(m => ({ default: m.BookConsult })));
const Contact          = lazy(() => import('./pages/Contact').then(m => ({ default: m.Contact })));
const ScopeOfCoaching  = lazy(() => import('./pages/legal/ScopeOfCoaching').then(m => ({ default: m.ScopeOfCoaching })));
const ClientAgreement  = lazy(() => import('./pages/legal/ClientAgreement').then(m => ({ default: m.ClientAgreement })));
const ParentalConsent  = lazy(() => import('./pages/legal/ParentalConsent').then(m => ({ default: m.ParentalConsent })));
const Privacy          = lazy(() => import('./pages/legal/Privacy').then(m => ({ default: m.Privacy })));
const Terms            = lazy(() => import('./pages/legal/Terms').then(m => ({ default: m.Terms })));
const Enroll           = lazy(() => import('./pages/Enroll').then(m => ({ default: m.Enroll })));
const EnrollSuccess    = lazy(() => import('./pages/EnrollSuccess').then(m => ({ default: m.EnrollSuccess })));
const Quiz             = lazy(() => import('./pages/Quiz').then(m => ({ default: m.Quiz })));
const NotFound         = lazy(() => import('./pages/NotFound').then(m => ({ default: m.NotFound })));

// Lightweight fallback — keeps layout calm during the (typically <100ms)
// chunk fetch. Intentionally invisible to avoid a layout flicker.
function RouteFallback() {
  return <div aria-hidden style={{ minHeight: '50vh' }} />;
}

export function App() {
  // Hide the global ScopeBand on the scope-of-coaching page itself, since
  // the band's "Read the full scope" link would just point at the same page.
  const { pathname } = useLocation();
  const showScopeBand = pathname !== '/legal/scope-of-coaching';

  return (
    <div className="flex min-h-screen flex-col bg-cream text-ink">
      <ScrollToTop />
      <Header />
      <main id="main" className="flex-1">
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/programs" element={<Programs />} />
            <Route path="/programs/:slug" element={<ProgramDetail />} />
            <Route path="/book-consult" element={<BookConsult />} />
            <Route path="/enroll" element={<Enroll />} />
            <Route path="/enroll/success" element={<EnrollSuccess />} />
            <Route path="/quiz" element={<Quiz />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/legal/scope-of-coaching" element={<ScopeOfCoaching />} />
            <Route path="/legal/client-agreement" element={<ClientAgreement />} />
            <Route path="/legal/parental-consent" element={<ParentalConsent />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
      {showScopeBand && <ScopeBand tone="prominent" />}
      <Footer />
    </div>
  );
}
